import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Box, Button } from '@mui/material';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { LoadingButton } from 'src/components/atoms/loading-button/loading-button';
import PasswordInput from 'src/components/atoms/password-input/password-input';
import { ApiError } from 'src/types/interfaces/api-error';

import AuthFormHeader from '../../organisms/auth-form-header';
import AuthFormWrapper from '../auth-form-wrapper';

import { usePasswordSchema } from './schema/password-schema';

import { useSetPassword } from '@/features/auth/hooks/mutation/use-set-password';
import { useResetPasswordExpiration } from '@/features/auth/hooks/query/use-reset-password-expiration';
import { useKeyDown } from '@/hooks/use-key-down';
import { Logo } from '@/icons/logo';

interface FormValues {
  password: string;
  passwordConfirmation: string;
}

const defaultValues: FormValues = {
  password: '',
  passwordConfirmation: '',
};

const SetUpPassword = () => {
  const { t } = useTranslation(['validation', 'auth', 'common']);
  const navigate = useNavigate({ from: '/set-up-password' });

  const { token } = useSearch({ from: '/(auth)/set-up-password' });

  const { data: isResetPasswordNotExpired, isError: isResetPasswordValidityError } =
    useResetPasswordExpiration(token, !!token);

  useEffect(() => {
    if (token && isResetPasswordNotExpired === undefined && !isResetPasswordValidityError) {
      return;
    }

    if (!token || !isResetPasswordNotExpired || isResetPasswordValidityError) {
      navigate({
        to: '/forgot-password',
        search: {
          expired: true,
        },
        replace: true,
      });
    }
  }, [isResetPasswordNotExpired, isResetPasswordValidityError, navigate, token]);

  const validationSchema = usePasswordSchema();

  const {
    control,
    formState: { errors, isSubmitting, isValid },
    setError,
    handleSubmit,
  } = useForm<FormValues>({
    resolver: zodResolver(validationSchema),
    mode: 'onBlur',
    defaultValues,
  });

  const { mutateAsync: updatePassword } = useSetPassword(() => {
    toast.success(t('auth:setUpPassword.setUpSuccess'));

    navigate({ to: '/login' });
  });

  const onSubmit = async ({ password }: FormValues) => {
    if (!token) {
      return;
    }

    try {
      await updatePassword({ password, token });
    } catch (error) {
      const authError = error as ApiError;

      setError('root', {
        message: authError?.response?.data?.message ?? t('common:somethingWentWrong'),
      });
    }
  };

  useKeyDown('Enter', errors.root ? () => {} : handleSubmit(onSubmit));

  return (
    <AuthFormWrapper>
      <AuthFormHeader
        title={t('auth:setUpPassword.title')}
        description={t('auth:setUpPassword.content')}
        icon={<Logo />}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Controller
          name="password"
          control={control}
          render={({ field: { onChange, onBlur, value, ref }, fieldState: { error } }) => (
            <PasswordInput
              label={t('auth:password')}
              fullWidth
              required
              inputRef={ref}
              onBlur={onBlur}
              onChange={onChange}
              value={value}
              type="password"
              autoComplete="password"
              error={!!error}
              helperText={error?.message}
              inputProps={{
                'data-testid': 'password-input',
              }}
            />
          )}
        />
        <Controller
          name="passwordConfirmation"
          control={control}
          render={({ field: { onChange, onBlur, value, ref }, fieldState: { error } }) => (
            <PasswordInput
              label={t('auth:passwordConfirm')}
              fullWidth
              required
              inputRef={ref}
              onBlur={onBlur}
              onChange={onChange}
              value={value}
              type="password"
              autoComplete="new-password"
              error={!!error}
              helperText={error?.message}
              inputProps={{
                'data-testid': 'confirm-password-input',
              }}
            />
          )}
        />
        {errors.root?.message ? <Alert severity="error">{errors?.root?.message}</Alert> : null}

        {errors.root ? (
          <Button
            variant="contained"
            size="medium"
            onClick={() => navigate({ to: '/forgot-password' })}
            sx={{ my: 2, width: '100%' }}>
            {t('auth:forgotPassword.resetButton')}
          </Button>
        ) : (
          <LoadingButton
            variant="contained"
            size="medium"
            disabled={!isValid}
            loading={isSubmitting}
            onClick={handleSubmit(onSubmit)}
            sx={{ my: 2, width: '100%' }}>
            {t('auth:setUpPassword.setPassword')}
          </LoadingButton>
        )}
      </Box>
    </AuthFormWrapper>
  );
};

export default SetUpPassword;
